import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, ConfigProvider, Form, Input, Modal, Popover, Typography, message, theme } from "antd";
import { CloseOutlined, CommentOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";
import apiClient from "../api/apiClient";
import API_CONFIG from "../config/api";
import { invalidateBusinessResources } from "../utils/businessResourceFreshness";
import { feedbackError, feedbackInboxScope, newFeedbackRequestId } from "../gestion-feedback/feedbackApi";
import { dismissFeedbackWidget, FEEDBACK_FORM_OPEN_EVENT, FEEDBACK_WIDGET_RESET_EVENT, isFeedbackWidgetDismissed } from "../gestion-feedback/feedbackWidgetSession";
import "../gestion-feedback/feedback.css";

interface OpenContext { key: string; companyId: number | null; warehouseId: number | null; path: string }

export default function FeedbackWidget({ blocked = false }: { blocked?: boolean }) {
  const { user } = useAuth();
  const { selectedCompany, selectedWarehouse } = useSelection();
  const location = useLocation();
  const { token } = theme.useToken();
  const client = useQueryClient();
  const [notice, noticeHolder] = message.useMessage();
  const isSysAdmin = Number(user?.is_superadmin) === 1;
  const companyId = Number(selectedCompany || (!isSysAdmin && user?.company_id)) || null;
  const warehouseId = Number(selectedWarehouse) || null;
  const contextKey = `${user?.id || ""}:${companyId || ""}:${warehouseId || ""}`;
  const currentContext = useRef(contextKey);
  currentContext.current = contextKey;
  const [open, setOpen] = useState(false);
  const [presentation, setPresentation] = useState<"bubble" | "profile">("bubble");
  const [dismissed, setDismissed] = useState(() => isFeedbackWidgetDismissed(user?.id));
  const [openedContext, setOpenedContext] = useState<OpenContext | null>(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const inFlight = useRef(false);
  const revision = useRef(0);
  const attempt = useRef<{ fingerprint: string; requestId: string } | null>(null);
  const mounted = useRef(true);
  const button = useRef<HTMLButtonElement>(null);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    setOpen(false); setPresentation("bubble"); setDraft(""); setError(""); attempt.current = null; revision.current++;
    setDismissed(isFeedbackWidgetDismissed(user?.id));
  }, [user?.id]);
  useEffect(() => {
    const reset = () => {
      setDismissed(false); setOpen(false); setPresentation("bubble"); setDraft(""); setError(""); attempt.current = null; revision.current++;
    };
    window.addEventListener(FEEDBACK_WIDGET_RESET_EVENT, reset);
    return () => window.removeEventListener(FEEDBACK_WIDGET_RESET_EVENT, reset);
  }, []);
  useEffect(() => { if (blocked) setOpen(false); }, [blocked]);
  const contextChanged = Boolean(openedContext && openedContext.key !== contextKey);

  const changeOpen = useCallback((next: boolean, source: "bubble" | "profile" = "bubble") => {
    if (next && !blocked) {
      setPresentation(source);
      setOpenedContext({ key: contextKey, companyId, warehouseId, path: location.pathname });
      setError("");
    }
    setOpen(next && !blocked);
  }, [blocked, contextKey, companyId, warehouseId, location.pathname]);

  useEffect(() => {
    const openFromProfile = (event: Event) => {
      const requestedUserId = (event as CustomEvent<{ userId: unknown }>).detail?.userId;
      if (user && !blocked && Number(requestedUserId) === Number(user.id)) changeOpen(true, "profile");
    };
    window.addEventListener(FEEDBACK_FORM_OPEN_EVENT, openFromProfile);
    return () => window.removeEventListener(FEEDBACK_FORM_OPEN_EVENT, openFromProfile);
  }, [user, blocked, changeOpen]);

  async function send() {
    if (inFlight.current || !openedContext || contextChanged) return;
    const text = draft.trim();
    if (!text) { setError("Saisissez votre suggestion ou le problème rencontré."); return; }
    if (!openedContext.companyId) { setError("Sélectionnez une entreprise avant d’envoyer votre avis."); return; }
    const payload = { message: text, company_id: openedContext.companyId,
      warehouse_id: openedContext.warehouseId, screen_path: openedContext.path };
    const fingerprint = JSON.stringify(payload);
    if (attempt.current?.fingerprint !== fingerprint) {
      attempt.current = { fingerprint, requestId: newFeedbackRequestId() };
    }
    const sentRevision = revision.current, sentContext = openedContext.key;
    inFlight.current = true; setSending(true); setError("");
    try {
      await apiClient.post(API_CONFIG.apiUrl("/feedback"), { ...payload, request_id: attempt.current.requestId },
        { headers: { "X-Company-Id": String(openedContext.companyId) } });
      void invalidateBusinessResources(client, feedbackInboxScope(user?.id), "feedback");
      if (mounted.current && currentContext.current === sentContext && revision.current === sentRevision) {
        setDraft(""); setOpen(false); attempt.current = null;
        notice.success("Merci ! Votre avis a bien été reçu.");
      }
    } catch (failure) {
      if (mounted.current && currentContext.current === sentContext) {
        const result = feedbackError(failure);
        setError(result.message);
        if (result.code === "FEEDBACK_REQUEST_CONFLICT") attempt.current = null;
      }
    } finally {
      inFlight.current = false;
      if (mounted.current) setSending(false);
    }
  }

  if (!user || blocked) return null;
  const content = <section id="feedback-form-dialog" role={presentation === "bubble" ? "dialog" : undefined}
    aria-labelledby={presentation === "bubble" ? "feedback-form-title" : undefined}
    className={`feedback-form${presentation === "profile" ? " feedback-form--profile" : ""}`}
    onKeyDown={(event) => { if (event.key === "Escape") { changeOpen(false); button.current?.focus(); } }}>
    {presentation === "bubble" && <div className="feedback-form__heading">
      <Typography.Title level={4} id="feedback-form-title">Donner votre avis</Typography.Title>
      <Button type="text" icon={<CloseOutlined />} aria-label="Fermer le formulaire d’avis" onClick={() => changeOpen(false)} />
    </div>}
    {contextChanged && <Alert type="warning" showIcon message="Le contexte a changé. Fermez puis rouvrez le formulaire pour envoyer cet avis dans le contexte actuel. Votre texte est conservé." />}
    <Form layout="vertical" onFinish={() => { void send(); }}>
      <Form.Item label="Comment pouvons-nous améliorer ELSA GESTION ?" htmlFor="feedback-message" required>
        <Input.TextArea id="feedback-message" value={draft} disabled={sending} maxLength={5000} autoFocus
          autoSize={{ minRows: 4, maxRows: 8 }} placeholder="Décrivez votre suggestion ou le problème rencontré…"
          onChange={(event) => { revision.current++; setDraft(event.target.value); setError(""); }} />
      </Form.Item>
      {error && <Alert type="error" showIcon message={error} className="feedback-form__error" />}
      <Button type="primary" htmlType="submit" block size="large" loading={sending}
        disabled={contextChanged || !companyId}>Envoyer mon avis</Button>
    </Form>
    <Typography.Text type="secondary" className="feedback-form__hint">Votre retour nous aide à améliorer ELSA GESTION.</Typography.Text>
    {!companyId && <Typography.Text type="warning">Sélectionnez une entreprise pour envoyer votre avis.</Typography.Text>}
  </section>;

  return <ConfigProvider theme={{ token: { colorTextSecondary: token.colorTextLabel,
    colorTextDescription: token.colorTextLabel, colorTextPlaceholder: token.colorTextLabel } }}>
    {noticeHolder}
    {presentation === "profile" && <Modal title={<Typography.Title level={4} style={{ margin: 0 }}>Donner votre avis</Typography.Title>}
      open={open} onCancel={() => changeOpen(false)}
      footer={null} width={440} destroyOnClose className="feedback-profile-modal" zIndex={token.zIndexPopupBase + 100}>
      {content}
    </Modal>}
    {!dismissed && <div className="feedback-widget" style={{ background: token.colorPrimary, boxShadow: token.boxShadowSecondary,
      "--feedback-widget-divider": token.colorPrimaryBorder } as React.CSSProperties}>
      <Popover placement="topRight" arrow={false} trigger="click" open={open && presentation === "bubble"}
        onOpenChange={(next) => { if (next || presentation === "bubble") changeOpen(next); }}
        content={presentation === "profile" && open ? null : content} overlayClassName="feedback-popover" destroyTooltipOnHide zIndex={token.zIndexPopupBase + 100}>
        <Button ref={button} type="primary" size="large" icon={<CommentOutlined />} aria-label="Donner mon avis"
          aria-expanded={open} aria-controls={open ? "feedback-form-dialog" : undefined} className="feedback-widget__button" style={{ boxShadow: "none" }}>
          <span className="feedback-widget__label">Donner mon avis</span>
        </Button>
      </Popover>
      <Button type="primary" size="large" icon={<CloseOutlined />} className="feedback-widget__dismiss" style={{ boxShadow: "none" }}
        aria-label="Masquer la bulle Donner mon avis" title="Masquer jusqu’à la prochaine connexion"
        onClick={() => { changeOpen(false); dismissFeedbackWidget(user.id); setDismissed(true); }} />
    </div>}
  </ConfigProvider>;
}
